import { useEffect, useState } from 'react'
import { nombreMessagesClientLivreur } from '../api/dispatch'
import { badgeStatut, libelleStatut, type Commande } from '../api/types'
import { peutAnnuler, raisonNonAnnulable } from '../api/annulation'
import { AnnulerCommande } from './AnnulerCommande'
import { decomposerLignes } from '../api/compta'
import { fcfa } from './Format'
import { Ligne, Panneau } from './Modale'

/**
 * Fiche d'une commande — TRANSPOSITION de `showOrder`.
 *
 * ⚠️ Elle manquait ENTIÈREMENT : la référence n'était même pas cliquable dans
 * la liste. C'est pourtant le geste central du dispatch — un client appelle en
 * donnant sa référence, et tout ce qu'il faut pour lui répondre est ici :
 * son téléphone, l'adresse, le livreur, et surtout le SUIVI horodaté.
 */
const dateHeure = (t: string | null) =>
  t
    ? new Date(t).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

/**
 * Le parcours réel de la commande, étape par étape.
 *
 * On ne garde que les étapes RÉELLEMENT franchies, et on les trie par
 * horodatage plutôt que par ordre théorique : « prête » et « livreur affecté »
 * arrivent dans n'importe quel ordre, et afficher une séquence fixe donnerait
 * une chronologie fausse.
 */
/** Taux figé (fraction) en texte : 0,1 → « 10 % », 0,125 → « 12,5 % ». */
const tauxTexte = (t: number) => `${String(Math.round(t * 1000) / 10).replace('.', ',')} %`

function suivi(o: Commande) {
  const etapes: Array<{ libelle: string; acteur: string; a: string | null }> = [
    { libelle: 'Créée · en attente de paiement', acteur: 'Client', a: o.creeeLe },
    { libelle: 'Acceptée', acteur: 'Marchand', a: o.accepteeLe },
    { libelle: 'En préparation', acteur: 'Marchand', a: o.enPreparationLe },
    { libelle: 'Livreur affecté', acteur: 'Dispatch / Livreur', a: o.livreurAffecteLe },
    { libelle: 'Prête', acteur: 'Marchand', a: o.preteLe },
    { libelle: 'Récupérée par le livreur', acteur: 'Livreur', a: o.retireeLe },
    { libelle: 'En route vers le client', acteur: 'Livreur', a: o.enLivraisonLe },
    { libelle: 'Livrée', acteur: 'Livreur', a: o.livreeLe },
    { libelle: 'Annulée', acteur: '—', a: o.annuleeLe },
  ]
  return etapes
    .filter((e): e is { libelle: string; acteur: string; a: string } => Boolean(e.a))
    .sort((a, b) => new Date(a.a).getTime() - new Date(b.a).getTime())
}

export function FicheCommande({
  commande,
  surFermer,
  surVoirDiscussion,
  surAnnulee,
}: {
  commande: Commande | null
  surFermer: () => void
  /** Ouvre le fil client ↔ livreur de la commande dans la messagerie. */
  surVoirDiscussion?: (commandeId: string) => void
  /** Si fourni, la fiche propose « Annuler la commande » ; appelée une fois l'annulation faite. */
  surAnnulee?: () => Promise<void>
}) {
  const [annulation, setAnnulation] = useState(false)
  const lignes = commande ? decomposerLignes(commande) : []
  /* Le lien n'existe QUE s'il y a quelque chose à lire : un lien vers un fil
     vide ferait chercher une conversation qui n'a jamais eu lieu. Nombre de
     messages client ↔ livreur de cette commande, 0 tant qu'on ne sait pas. */
  const [nbEchanges, setNbEchanges] = useState(0)
  const idCommande = commande?.id ?? null
  useEffect(() => {
    setNbEchanges(0)
    if (!idCommande) return
    let vivant = true
    nombreMessagesClientLivreur(idCommande)
      .then((n) => vivant && setNbEchanges(n))
      .catch(() => undefined) // lien absent plutôt qu'erreur : la fiche reste utilisable
    return () => {
      vivant = false
    }
  }, [idCommande])

  return (
    <Panneau
      titre={commande ? `Commande ${commande.ref ?? ''}` : ''}
      ouvert={Boolean(commande)}
      surFermer={surFermer}
    >
      {commande && (
        <>
          <Ligne libelle="Reference">{commande.ref}</Ligne>
          <Ligne libelle="Client">{commande.clientNom}</Ligne>
          <Ligne libelle="Telephone">
            {/* Un lien `tel:` et non du texte : sur un poste de dispatch relié
                à un softphone, c'est un clic au lieu d'une recopie à la main —
                et une recopie à la main se trompe de chiffre. */}
            <a href={`tel:${commande.clientTelephone}`}>{commande.clientTelephone || '—'}</a>
          </Ligne>
          <Ligne libelle="Marchand">{commande.marchandNom}</Ligne>
          <Ligne libelle="Livreur">{commande.livreurNom || 'Non assigne'}</Ligne>
          <Ligne libelle="Statut">
            <span className={`badge ${badgeStatut(commande.statut)}`}>
              {libelleStatut(commande.statut)}
            </span>
          </Ligne>
          {nbEchanges > 0 && surVoirDiscussion && (
            <Ligne libelle="Discussion">
              <a
                href="#discussion-client-livreur"
                onClick={(e) => {
                  e.preventDefault()
                  surVoirDiscussion(commande.id)
                }}
              >
                Voir la discussion client {'\u2194'} livreur
              </a>{' '}
              <span style={{ color: 'var(--n400)' }}>
                ({nbEchanges} message{nbEchanges > 1 ? 's' : ''})
              </span>
            </Ligne>
          )}
          <Ligne libelle="Total">{fcfa(commande.total)}</Ligne>
          <Ligne libelle="Frais livraison">{fcfa(commande.fraisLivraison)}</Ligne>
          <Ligne libelle="Frais service">{fcfa(commande.fraisService)}</Ligne>
          {(commande.fraisTension ?? 0) > 0 && (
            <Ligne libelle="Forte demande">
              {`+${fcfa(commande.fraisTension ?? 0)}`}
              <span style={{ color: 'var(--n400)' }}>
                {` (dont ${fcfa(commande.partTensionLivreur ?? 0)} au livreur)`}
              </span>
            </Ligne>
          )}

          <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Adresse de livraison</h4>
          <Ligne libelle="Adresse">
            {commande.adresseLivraison || commande.clientQuartier || 'Adresse non précisée'}
          </Ligne>
          {commande.communeLivraison && (
            <Ligne libelle="Commune">{commande.communeLivraison}</Ligne>
          )}
          {commande.clientQuartier && <Ligne libelle="Quartier">{commande.clientQuartier}</Ligne>}
          {commande.noteLivraison && <Ligne libelle="Note client">{commande.noteLivraison}</Ligne>}

          {commande.articles.length > 0 && (
            <>
              <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Articles</h4>
              {/* 0055 : décomposition complète, ligne par ligne — ce que paie le client, ce qui revient au
                  marchand, la commission et le taux figé. Une commande d'avant 0055 (commission retenue) se lit
                  avec les mêmes trois colonnes. */}
              <table>
                <thead>
                  <tr>
                    <th>Article</th>
                    <th>Qté</th>
                    <th style={{ textAlign: 'right' }}>Prix client</th>
                    <th style={{ textAlign: 'right' }}>Prix marchand</th>
                    <th style={{ textAlign: 'right' }}>Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => (
                    <tr key={i}>
                      <td>{l.nom}</td>
                      <td>x{l.quantite}</td>
                      <td style={{ textAlign: 'right' }}>{fcfa(l.prixClient)}</td>
                      <td style={{ textAlign: 'right' }}>{fcfa(l.prixMarchand)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {fcfa(l.commission)}{' '}
                        <span style={{ color: 'var(--n400)' }}>({tauxTexte(l.taux)})</span>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={2}>Total articles</td>
                    <td style={{ textAlign: 'right' }}>{fcfa(lignes.reduce((s, l) => s + l.prixClient, 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fcfa(lignes.reduce((s, l) => s + l.prixMarchand, 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fcfa(lignes.reduce((s, l) => s + l.commission, 0))}</td>
                  </tr>
                </tbody>
              </table>
              <p className="aide">
                {lignes.some((l) => !l.ajoutee)
                  ? 'Commande d’avant le 12/10/2026 : la commission était retenue sur le prix payé par le client.'
                  : 'Le client paie le prix du marchand plus la commission ; le marchand reçoit son prix.'}
              </p>
            </>
          )}

          {surAnnulee && (
            <div style={{ marginTop: 16 }}>
              {peutAnnuler(commande.statut) ? (
                <button className="btn btn-danger-sm btn-sm" onClick={() => setAnnulation(true)}>
                  Annuler la commande
                </button>
              ) : (
                <p style={{ fontSize: 12, color: 'var(--n400)', margin: 0 }}>{raisonNonAnnulable(commande.statut)}</p>
              )}
            </div>
          )}

          <h4 style={{ margin: '16px 0 8px', fontSize: 13 }}>Suivi (horodatage par étape)</h4>
          <table>
            <tbody>
              {suivi(commande).map((e, i) => (
                <tr key={i}>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateHeure(e.a)}</td>
                  <td>{e.libelle}</td>
                  {/* L'ACTEUR de chaque étape : c'est ce qui évite d'appeler le
                      marchand pour une action qui revient au livreur. */}
                  <td style={{ color: 'var(--n400)' }}>{e.acteur}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {surAnnulee && (
        <AnnulerCommande
          commande={annulation ? commande : null}
          surFermer={() => setAnnulation(false)}
          surAnnulee={async () => {
            setAnnulation(false)
            await surAnnulee()
            surFermer()
          }}
        />
      )}
    </Panneau>
  )
}
